/**
 * Per-session image (photo) view tracking — replaces per-photo Slack spam with
 * a single consolidated session summary and per-photo dwell measurement.
 *
 * Model:
 *   - One photo is "active" at a time (the open lightbox image).
 *   - Dwell is measured from open until: another photo opens, the tab hides,
 *     the page unloads, or 45s of inactivity.
 *   - The whole browsing session is accumulated in sessionStorage (per photo:
 *     opens + total dwell).
 *   - On leaving a photo we POST that photo's row (`type:"view"`, no Slack).
 *   - On tab hide / pagehide / inactivity we POST ONE `type:"session"` summary
 *     (all photos + per-photo dwell + totals). The summary is deduped by a
 *     signature so an unchanged session is never resent.
 *
 * Transport mirrors `mirrorFunnelEvent`: fetch `keepalive` with a `sendBeacon`
 * fallback so requests survive tab close.
 */

import { BRAND_SITE_HOST } from "@/lib/brandConfig";
import { trafficSourceFunnelProps } from "@/lib/detectTrafficSource";
import {
  getPostHogCorrelationIdsPostHog,
  getPostHogRequestHeadersPostHog,
} from "@/lib/posthogAnalytics";
import {
  resolveInternationalHomeVipCheckoutPricing,
  type InternationalHomeVipCheckoutPricing,
} from "@/lib/resolveInternationalHomeOffer";
import type {
  InternationalHomeVipPricing,
  InternationalHomeVipPricingByCountry,
} from "@/lib/internationalHomeTemplate";

const SESSION_KEY = "anaa_image_session_v1";
const SIG_KEY = "anaa_image_session_sig_v1";
const INACTIVITY_MS = 45_000;
const ENDPOINT = "/api/track-image-view";

export interface ImageViewContext {
  templateName: string;
  countryCode?: string;
  language?: string;
}

export interface TrackedPhoto {
  /** Stable per-photo key (Cloudinary public_id or src). */
  publicId: string;
  title?: string;
  /** Numeric/string gallery id (for cross-reference). */
  photoId?: string | number;
}

interface PhotoAccum {
  publicId: string;
  title: string;
  photoId: string;
  opens: number;
  dwellMs: number;
}

interface SessionAccum {
  photos: Record<string, PhotoAccum>;
  startedAt: number;
}

interface ActivePhoto {
  publicId: string;
  title: string;
  photoId: string;
  openedAt: number;
}

let active: ActivePhoto | null = null;
let ctx: ImageViewContext = { templateName: "BaseTemplate" };
let inactivityTimer: ReturnType<typeof setTimeout> | null = null;
let listenersAttached = false;

function now(): number {
  return typeof performance !== "undefined" ? performance.now() : Date.now();
}

function readSession(): SessionAccum {
  try {
    const raw = sessionStorage.getItem(SESSION_KEY);
    if (raw) {
      const o = JSON.parse(raw) as Partial<SessionAccum>;
      if (o && typeof o === "object" && o.photos && typeof o.photos === "object") {
        return { photos: o.photos as Record<string, PhotoAccum>, startedAt: o.startedAt ?? Date.now() };
      }
    }
  } catch {
    /* stale / private mode */
  }
  return { photos: {}, startedAt: Date.now() };
}

function writeSession(s: SessionAccum): void {
  try {
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(s));
  } catch {
    /* quota / private mode */
  }
}

function postImageTrack(payload: Record<string, unknown>): void {
  if (typeof window === "undefined") return;
  const corr = (() => {
    try {
      return getPostHogCorrelationIdsPostHog();
    } catch {
      return { ph_distinct_id: null, ph_session_replay_url: null };
    }
  })();

  const ph = (
    window as unknown as {
      posthog?: { get_distinct_id?: () => string; get_session_id?: () => string };
    }
  ).posthog;

  const enriched = {
    ...payload,
    posthog_distinct_id:
      (payload.posthog_distinct_id as string | undefined) ||
      ph?.get_distinct_id?.() ||
      corr.ph_distinct_id ||
      undefined,
    session_id:
      (payload.session_id as string | undefined) || ph?.get_session_id?.() || undefined,
    template: ctx.templateName,
    country: ctx.countryCode,
    language: ctx.language,
    page_url: window.location.href.slice(0, 2048),
    props: {
      ...((payload.props as Record<string, unknown> | undefined) ?? {}),
      ph_distinct_id: corr.ph_distinct_id ?? undefined,
      ph_session_replay_url: corr.ph_session_replay_url ?? undefined,
      brand_host: BRAND_SITE_HOST,
    },
  };

  const body = JSON.stringify(enriched);
  const url = `${window.location.origin}${ENDPOINT}`;
  try {
    void fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...getPostHogRequestHeadersPostHog() },
      keepalive: true,
      body,
    }).catch(() => beacon(url, body));
  } catch {
    beacon(url, body);
  }
}

function beacon(url: string, body: string): void {
  try {
    if (typeof navigator.sendBeacon === "function") {
      navigator.sendBeacon(url, new Blob([body], { type: "application/json" }));
    }
  } catch {
    /* noop */
  }
}

function clearInactivity(): void {
  if (inactivityTimer !== null) {
    clearTimeout(inactivityTimer);
    inactivityTimer = null;
  }
}

function armInactivity(): void {
  clearInactivity();
  inactivityTimer = setTimeout(() => {
    finalizeActive("inactivity");
    sendImageSessionSummary("inactivity");
  }, INACTIVITY_MS);
}

/** Write the just-ended photo's dwell row (type:"view", no Slack) and accumulate. */
function finalizeActive(reason: string): void {
  clearInactivity();
  const a = active;
  active = null;
  if (!a) return;

  const dwellMs = Math.max(0, Math.round(now() - a.openedAt));

  const s = readSession();
  const entry =
    s.photos[a.publicId] ??
    ({ publicId: a.publicId, title: a.title, photoId: a.photoId, opens: 0, dwellMs: 0 } as PhotoAccum);
  entry.dwellMs += dwellMs;
  entry.title = a.title || entry.title;
  entry.photoId = a.photoId || entry.photoId;
  s.photos[a.publicId] = entry;
  writeSession(s);

  postImageTrack({
    type: "view",
    props: {
      public_id: a.publicId,
      photo_title: a.title || undefined,
      photo_id: a.photoId || undefined,
      dwell_ms: dwellMs,
      opens_so_far: entry.opens,
      total_dwell_ms: entry.dwellMs,
      exit_reason: reason,
    },
  });
}

function attachListeners(): void {
  if (listenersAttached || typeof window === "undefined") return;
  listenersAttached = true;

  const onActivity = () => {
    if (active) armInactivity();
  };
  for (const ev of ["pointerdown", "pointermove", "keydown", "scroll", "touchstart"]) {
    window.addEventListener(ev, onActivity, { passive: true });
  }

  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") {
      finalizeActive("hidden");
      sendImageSessionSummary("hidden");
    }
  });

  window.addEventListener("pagehide", () => {
    finalizeActive("pagehide");
    sendImageSessionSummary("pagehide");
  });
}

/** Call when a photo lightbox opens (or navigates to a different photo). */
export function trackImageOpen(photo: TrackedPhoto, context?: ImageViewContext): void {
  if (typeof window === "undefined") return;
  const publicId = String(photo.publicId ?? "").trim();
  if (!publicId) return;
  if (context) ctx = { ...ctx, ...context };
  attachListeners();

  // Leaving the previous photo (switch) finalizes its dwell first.
  if (active && active.publicId !== publicId) finalizeActive("switch");

  const title = String(photo.title ?? "").trim();
  const photoId = photo.photoId != null ? String(photo.photoId) : "";

  const s = readSession();
  const entry =
    s.photos[publicId] ??
    ({ publicId, title, photoId, opens: 0, dwellMs: 0 } as PhotoAccum);
  entry.opens += 1;
  if (title) entry.title = title;
  if (photoId) entry.photoId = photoId;
  s.photos[publicId] = entry;
  writeSession(s);

  active = { publicId, title, photoId, openedAt: now() };
  armInactivity();
}

/** Call when the lightbox closes (X / tap-out). Finalizes the active photo's dwell. */
export function trackImageClose(reason = "close"): void {
  finalizeActive(reason);
}

/** Build a stable signature so an unchanged summary is not resent. */
function sessionSignature(s: SessionAccum): string {
  return Object.values(s.photos)
    .sort((a, b) => a.publicId.localeCompare(b.publicId))
    .map((p) => `${p.publicId}:${p.opens}:${Math.round(p.dwellMs / 1000)}`)
    .join("|");
}

/** Device / traffic / pricing context for the Slack session report. */
function buildSessionClientExtras(): Record<string, unknown> {
  const extras: Record<string, unknown> = {
    path: window.location.pathname.slice(0, 200),
    brand_host: BRAND_SITE_HOST,
  };

  try {
    extras.screen = `${window.screen?.width ?? "?"}x${window.screen?.height ?? "?"}`;
    extras.viewport = `${window.innerWidth}x${window.innerHeight}`;
    extras.timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || undefined;
  } catch {
    /* ignore */
  }

  try {
    const ts = trafficSourceFunnelProps() as Record<string, unknown>;
    if (ts.traffic_channel) extras.traffic_channel = ts.traffic_channel;
    if (ts.traffic_source) extras.traffic_source = ts.traffic_source;
    if (ts.traffic_medium) extras.traffic_medium = ts.traffic_medium;
    if (ts.utm_source) extras.utm_source = ts.utm_source;
    if (ts.utm_medium) extras.utm_medium = ts.utm_medium;
    if (ts.utm_campaign) extras.utm_campaign = ts.utm_campaign;
    if (ts.utm_content) extras.utm_content = ts.utm_content;
    if (ts.traffic_in_app === true) extras.in_app_browser = true;
  } catch {
    /* ignore */
  }

  // VIP membership offer — SAME resolver as DualCTA / invitacion-exclusiva / checkout.
  // Never use pack "exclusive" ($39.99) or PROMO50 (50%) here — that was incoherent.
  try {
    const pricing = resolveVipOfferForImageSlack(ctx.countryCode);
    extras.vip_list_price_usd = pricing.fullPriceUsd;
    extras.vip_first_month_price_usd = pricing.amountCents / 100;
    extras.vip_discount_pct = pricing.discountPct;
    extras.vip_stripe_price_id = pricing.stripePriceId;
    extras.vip_promotion_code_id = pricing.promotionCodeId;
    extras.vip_price_source = pricing.priceSource;
  } catch {
    /* optional */
  }

  return extras;
}

/**
 * Read public site-settings VIP map from the same sessionStorage cache as
 * useSiteSettings (when already warm). Falls back to global FIRSTANA offer.
 */
function readCachedVipPricing(): {
  byCountry: InternationalHomeVipPricingByCountry | null;
  defaultEntry: InternationalHomeVipPricing | null;
} {
  if (typeof sessionStorage === "undefined") {
    return { byCountry: null, defaultEntry: null };
  }
  try {
    // useSiteSettings cache key prefix: aaf_site_settings_cache_v2_
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (!key || !key.startsWith("aaf_site_settings_cache_v2_")) continue;
      const raw = sessionStorage.getItem(key);
      if (!raw) continue;
      const blob = JSON.parse(raw) as {
        settings?: {
          internationalHomeVipPricingByCountry?: InternationalHomeVipPricingByCountry;
          internationalHomeVipPricingDefault?: InternationalHomeVipPricing | null;
        };
      };
      const s = blob?.settings;
      if (!s) continue;
      return {
        byCountry: s.internationalHomeVipPricingByCountry ?? null,
        defaultEntry: s.internationalHomeVipPricingDefault ?? null,
      };
    }
  } catch {
    /* ignore */
  }
  return { byCountry: null, defaultEntry: null };
}

function resolveVipOfferForImageSlack(
  country?: string | null,
): InternationalHomeVipCheckoutPricing {
  const cached = readCachedVipPricing();
  return resolveInternationalHomeVipCheckoutPricing({
    country: country || null,
    vipPricingByCountry: cached.byCountry,
    vipPricingDefault: cached.defaultEntry,
    language:
      typeof navigator !== "undefined" ? navigator.language : "es",
    defaultProductName: "VIP Membership",
  });
}

/** Send ONE consolidated session summary (deduped by signature). */
export function sendImageSessionSummary(reason: string): void {
  if (typeof window === "undefined") return;
  const s = readSession();
  const photos = Object.values(s.photos);
  if (photos.length === 0) return;

  const sig = sessionSignature(s);
  try {
    if (sessionStorage.getItem(SIG_KEY) === sig) return; // unchanged → skip
  } catch {
    /* ignore */
  }

  const totalOpens = photos.reduce((n, p) => n + p.opens, 0);
  const totalDwellMs = photos.reduce((n, p) => n + p.dwellMs, 0);
  const sessionDurationMs = Math.max(0, Date.now() - s.startedAt);

  postImageTrack({
    type: "session",
    props: {
      exit_reason: reason,
      photo_count: photos.length,
      total_opens: totalOpens,
      total_dwell_ms: totalDwellMs,
      session_duration_ms: sessionDurationMs,
      photos: photos
        .slice()
        .sort((a, b) => b.dwellMs - a.dwellMs)
        .map((p) => ({
          public_id: p.publicId,
          title: p.title || undefined,
          photo_id: p.photoId || undefined,
          opens: p.opens,
          dwell_ms: p.dwellMs,
        })),
      ...buildSessionClientExtras(),
    },
  });

  try {
    sessionStorage.setItem(SIG_KEY, sig);
  } catch {
    /* ignore */
  }
}
